import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ProdutoVarianteModificadorService } from '../../../services/produto-variante-modificador/produto-variante-modificador.service';
import {
  VarianteDePrecoDeItemDTO,
  GrupoDeModificadorDePrecoDeItemDTO,
  ModificadorDePrecoDeItemDTO,
  PersonalizacaoDeItemSelecionada,
} from '../../../models/produto-variante-modificador/produto-variante-modificador.model';
import {
  VarianteDePrecoDeItemPedidoDTO,
  ModificadorDePrecoDeItemPedidoDTO,
} from '../../../models/pedidos/pedido.model';

/**
 * Dialog de selecao de VARIANTE e MODIFICADORES de um produto, extraido do fluxo
 * do PDV (tela de Vendas) para ser reaproveitado em outras telas (ex.: Criar
 * Pedido) sem duplicar a logica de negocio.
 *
 * Fluxo:
 *  1. O pai seta `[produto]` com o produto clicado. O componente busca variantes
 *     e grupos de modificadores (com cache por item).
 *  2. Se o produto NAO tem variantes nem modificadores -> emite `(semOpcoes)`;
 *     o pai adiciona o produto direto ao carrinho.
 *  3. Se tem, abre o dialog. Ao confirmar -> emite `(confirmar)` com a
 *     personalizacao (preco ajustado + arrays no formato dos DTOs do pedido).
 *  4. Ao cancelar -> emite `(cancelar)` e nada e adicionado.
 *
 * O preco final por unidade = preco da variante (ou preco base) + soma dos
 * adicionais selecionados (preco * quantidade). As quantidades emitidas sao por
 * unidade; quem consome multiplica pela quantidade da linha do carrinho.
 */
@Component({
  selector: 'app-variante-modificador-dialog',
  templateUrl: './variante-modificador-dialog.component.html',
  styleUrls: ['./variante-modificador-dialog.component.scss'],
})
export class VarianteModificadorDialogComponent implements OnChanges {
  /** Produto a personalizar. Ao mudar (nao nulo), dispara a busca/decisao. */
  @Input() produto: any | null = null;
  @Input() idEmpresa: string | undefined;
  @Input() moedaPadrao = 'PYG';
  @Input() digitsMoeda = '1.2-2';
  @Input() localeMoeda = 'pt-BR';

  /** Produto nao possui variantes nem modificadores (adicionar direto). */
  @Output() semOpcoes = new EventEmitter<any>();
  /** Personalizacao confirmada pelo usuario. */
  @Output() confirmar = new EventEmitter<PersonalizacaoDeItemSelecionada>();
  /** Usuario fechou/cancelou o dialog sem confirmar. */
  @Output() cancelar = new EventEmitter<void>();

  env = environment;

  // Estado do dialog
  visible = false;
  carregando = false;
  produtoAtual: any = null;
  variantesDisponiveis: VarianteDePrecoDeItemDTO[] = [];
  gruposModificadores: GrupoDeModificadorDePrecoDeItemDTO[] = [];
  varianteSelecionada: VarianteDePrecoDeItemDTO | null = null;
  // grupoId -> (modificadorId -> quantidade)
  modificadoresQuantidade: Map<number, Map<number, number>> = new Map();
  precoCalculado = 0;
  observacao = '';

  // Cache por item_id para evitar chamadas HTTP repetidas.
  private cacheVariantes = new Map<number, VarianteDePrecoDeItemDTO[]>();
  private cacheGrupos = new Map<number, GrupoDeModificadorDePrecoDeItemDTO[]>();

  constructor(
    private translate: TranslateService,
    private produtoVarianteModificadorService: ProdutoVarianteModificadorService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['produto']) {
      const produto = changes['produto'].currentValue;
      if (produto) {
        this.iniciar(produto);
      }
    }
  }

  /** Busca variantes/grupos do produto e decide entre abrir o dialog ou emitir semOpcoes. */
  private iniciar(produto: any): void {
    this.produtoAtual = produto;
    this.varianteSelecionada = null;
    this.modificadoresQuantidade = new Map();
    this.observacao = '';
    this.precoCalculado = 0;

    const itemId = produto.id;
    const variantesCache = this.cacheVariantes.get(itemId);
    const gruposCache = this.cacheGrupos.get(itemId);

    if (variantesCache !== undefined && gruposCache !== undefined) {
      this.processar(produto, variantesCache, gruposCache);
      return;
    }

    this.carregando = true;
    forkJoin({
      variantes: this.produtoVarianteModificadorService.getVariantes(itemId).pipe(
        catchError(() => of([] as VarianteDePrecoDeItemDTO[])),
      ),
      grupos: this.produtoVarianteModificadorService.getGrupos(itemId).pipe(
        catchError(() => of([] as GrupoDeModificadorDePrecoDeItemDTO[])),
      ),
    }).subscribe(({ variantes, grupos }) => {
      this.carregando = false;
      const listaVariantes = Array.isArray(variantes) ? variantes : [];
      const listaGrupos = Array.isArray(grupos) ? grupos : [];
      this.cacheVariantes.set(itemId, listaVariantes);
      this.cacheGrupos.set(itemId, listaGrupos);
      this.processar(produto, listaVariantes, listaGrupos);
    });
  }

  private processar(
    produto: any,
    variantes: VarianteDePrecoDeItemDTO[],
    grupos: GrupoDeModificadorDePrecoDeItemDTO[],
  ): void {
    const temVariantes = variantes && variantes.length > 0;
    const temModificadores = grupos && grupos.length > 0;

    if (temVariantes || temModificadores) {
      this.variantesDisponiveis = variantes || [];
      this.gruposModificadores = grupos || [];
      this.calcularPreco();
      this.visible = true;
    } else {
      // Produto simples: o pai adiciona direto.
      this.semOpcoes.emit(produto);
    }
  }

  private fechar(): void {
    this.visible = false;
    this.produtoAtual = null;
    this.variantesDisponiveis = [];
    this.gruposModificadores = [];
    this.varianteSelecionada = null;
    this.modificadoresQuantidade = new Map();
    this.precoCalculado = 0;
    this.observacao = '';
  }

  // ---------------------------------------------------------------------------
  // Selecao
  // ---------------------------------------------------------------------------

  selecionarVariante(variante: VarianteDePrecoDeItemDTO): void {
    this.varianteSelecionada = variante;
    this.calcularPreco();
  }

  toggleModificador(
    grupo: GrupoDeModificadorDePrecoDeItemDTO,
    modificador: ModificadorDePrecoDeItemDTO,
  ): void {
    if (grupo.maximoDeModificadores === 1) {
      // Radio: substituir selecao no grupo.
      const quantidades = this.modificadoresQuantidade.get(grupo.id!) || new Map();
      const jaSelecionado = (quantidades.get(modificador.id!) || 0) > 0;
      quantidades.clear();
      if (!jaSelecionado) {
        quantidades.set(modificador.id!, 1);
      }
      this.modificadoresQuantidade.set(grupo.id!, new Map(quantidades));
    } else {
      const qtdAtual = this.getQuantidadeModificador(grupo.id!, modificador.id!);
      if (qtdAtual === 0) {
        this.incrementarModificador(grupo, modificador);
      } else {
        this.setQuantidadeModificador(grupo.id!, modificador.id!, 0);
      }
    }
    this.calcularPreco();
  }

  incrementarModificador(
    grupo: GrupoDeModificadorDePrecoDeItemDTO,
    modificador: ModificadorDePrecoDeItemDTO,
  ): void {
    const totalGrupo = this.getTotalSelecoesGrupo(grupo.id!);
    if (totalGrupo >= grupo.maximoDeModificadores) return;

    const qtdAtual = this.getQuantidadeModificador(grupo.id!, modificador.id!);
    this.setQuantidadeModificador(grupo.id!, modificador.id!, qtdAtual + 1);
    this.calcularPreco();
  }

  decrementarModificador(grupoId: number, modificadorId: number): void {
    const qtdAtual = this.getQuantidadeModificador(grupoId, modificadorId);
    if (qtdAtual <= 0) return;
    this.setQuantidadeModificador(grupoId, modificadorId, qtdAtual - 1);
    this.calcularPreco();
  }

  getQuantidadeModificador(grupoId: number, modificadorId: number): number {
    return this.modificadoresQuantidade.get(grupoId)?.get(modificadorId) || 0;
  }

  private setQuantidadeModificador(grupoId: number, modificadorId: number, quantidade: number): void {
    let quantidades = this.modificadoresQuantidade.get(grupoId);
    if (!quantidades) {
      quantidades = new Map();
      this.modificadoresQuantidade.set(grupoId, quantidades);
    }
    if (quantidade <= 0) {
      quantidades.delete(modificadorId);
    } else {
      quantidades.set(modificadorId, quantidade);
    }
  }

  isModificadorSelecionado(grupoId: number, modificadorId: number): boolean {
    return this.getQuantidadeModificador(grupoId, modificadorId) > 0;
  }

  getTotalSelecoesGrupo(grupoId: number): number {
    const quantidades = this.modificadoresQuantidade.get(grupoId);
    if (!quantidades) return 0;
    let total = 0;
    quantidades.forEach((qty) => (total += qty));
    return total;
  }

  isGrupoMaxAtingido(grupoId: number, maximoDeModificadores: number): boolean {
    return this.getTotalSelecoesGrupo(grupoId) >= maximoDeModificadores;
  }

  isGrupoComMinimoPendente(grupo: GrupoDeModificadorDePrecoDeItemDTO): boolean {
    if (grupo.minimoDeModificadores <= 0) return false;
    return this.getTotalSelecoesGrupo(grupo.id!) < grupo.minimoDeModificadores;
  }

  // ---------------------------------------------------------------------------
  // Preco / validacao
  // ---------------------------------------------------------------------------

  calcularPreco(): void {
    const precoBase = Number(this.produtoAtual?.precoDeVenda ?? 0);
    const precoVariante = this.varianteSelecionada
      ? this.varianteSelecionada.precoDeVenda
      : precoBase;

    let totalModificadores = 0;
    for (const grupo of this.gruposModificadores) {
      const quantidades = this.modificadoresQuantidade.get(grupo.id!);
      if (!quantidades) continue;
      for (const mod of grupo.modificadores) {
        const qty = quantidades.get(mod.id!) || 0;
        totalModificadores += mod.precoDeVenda * qty;
      }
    }

    this.precoCalculado = precoVariante + totalModificadores;
  }

  get podeConfirmar(): boolean {
    if (this.variantesDisponiveis.length > 0 && !this.varianteSelecionada) {
      return false;
    }
    for (const grupo of this.gruposModificadores) {
      if (grupo.minimoDeModificadores > 0) {
        const total = this.getTotalSelecoesGrupo(grupo.id!);
        if (total < grupo.minimoDeModificadores) {
          return false;
        }
      }
    }
    return true;
  }

  get mensagemValidacao(): string {
    if (this.variantesDisponiveis.length > 0 && !this.varianteSelecionada) {
      return this.translate.instant('vendas.variantes.selecione_variante_para_continuar');
    }
    for (const grupo of this.gruposModificadores) {
      if (grupo.minimoDeModificadores > 0) {
        const total = this.getTotalSelecoesGrupo(grupo.id!);
        if (total < grupo.minimoDeModificadores) {
          return this.translate.instant('vendas.variantes.selecione_modificadores_para_continuar');
        }
      }
    }
    return '';
  }

  // Breakdown do preco para exibicao detalhada.
  get breakdownPreco(): { descricao: string; valor: number; tipo: string; quantidade?: number }[] {
    const items: { descricao: string; valor: number; tipo: string; quantidade?: number }[] = [];

    if (this.varianteSelecionada) {
      items.push({
        descricao: this.varianteSelecionada.descricao,
        valor: this.varianteSelecionada.precoDeVenda,
        tipo: 'variante',
      });
    } else if (this.produtoAtual) {
      items.push({
        descricao: this.produtoAtual.nome,
        valor: Number(this.produtoAtual.precoDeVenda ?? 0),
        tipo: 'base',
      });
    }

    for (const grupo of this.gruposModificadores) {
      const quantidades = this.modificadoresQuantidade.get(grupo.id!);
      if (!quantidades) continue;
      for (const mod of grupo.modificadores) {
        const qty = quantidades.get(mod.id!) || 0;
        if (qty > 0) {
          items.push({
            descricao: mod.descricao,
            valor: mod.precoDeVenda * qty,
            tipo: 'modificador',
            quantidade: qty,
          });
        }
      }
    }

    return items;
  }

  // ---------------------------------------------------------------------------
  // Acoes
  // ---------------------------------------------------------------------------

  confirmarSelecao(): void {
    if (!this.podeConfirmar || !this.produtoAtual) return;

    // Variante selecionada (quantidade por unidade = 1).
    const variantesDePreco: VarianteDePrecoDeItemPedidoDTO[] = this.varianteSelecionada
      ? [
          {
            codigoDaVariante: String(this.varianteSelecionada.id),
            descricao: this.varianteSelecionada.descricao,
            quantidade: 1,
            precoDeVenda: this.varianteSelecionada.precoDeVenda,
            precoTotal: this.varianteSelecionada.precoDeVenda,
          },
        ]
      : [];

    // Modificadores selecionados (quantidade por unidade = adicionais escolhidos).
    const modificadores: ModificadorDePrecoDeItemPedidoDTO[] = [];
    const descricoes: string[] = [];
    for (const grupo of this.gruposModificadores) {
      const quantidades = this.modificadoresQuantidade.get(grupo.id!);
      if (!quantidades) continue;
      for (const mod of grupo.modificadores) {
        const qty = quantidades.get(mod.id!) || 0;
        if (qty > 0) {
          modificadores.push({
            codigoDoModificador: String(mod.id),
            descricao: mod.descricao,
            quantidade: qty,
            precoDeVenda: mod.precoDeVenda,
            precoTotal: mod.precoDeVenda * qty,
          });
          descricoes.push(qty > 1 ? `${mod.descricao} (x${qty})` : mod.descricao);
        }
      }
    }

    const resultado: PersonalizacaoDeItemSelecionada = {
      precoUnitario: this.precoCalculado,
      variantesDePreco,
      modificadores,
      varianteDescricao: this.varianteSelecionada?.descricao || null,
      modificadoresDescricao: descricoes.length ? descricoes.join(', ') : null,
      observacao: (this.observacao || '').trim(),
    };

    this.confirmar.emit(resultado);
    this.fechar();
  }

  cancelarSelecao(): void {
    this.fechar();
    this.cancelar.emit();
  }

  onHide(): void {
    if (this.visible) {
      this.cancelarSelecao();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible) this.cancelarSelecao();
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnter(event: KeyboardEvent): void {
    if (!this.visible) return;
    // Enter dentro do textarea de observação insere quebra de linha (não confirma).
    const alvo = event.target as HTMLElement | null;
    if (alvo && alvo.tagName === 'TEXTAREA') return;
    if (this.podeConfirmar) {
      event.preventDefault();
      this.confirmarSelecao();
    }
  }
}
